// src/admin/pages/QuestionsAdminPage.js
// Admin view for managing both Dealroom and Support questions
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  HelpCircle, 
  Folder,
  Search,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import simpleQuestionsApi from '../../products/questions/services/simpleQuestionsApi';
import { useGlobalStore } from '../../state/globalStore';
import { formatDate } from '../../products/questions/utils';

const QuestionsAdminPage = () => {
  const navigate = useNavigate();
  const { user } = useGlobalStore();
  const [activeTab, setActiveTab] = useState('dealroom');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusFilter]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const filters = {
        module_type: activeTab === 'dealroom' ? 'dataroom' : 'general',
        status: statusFilter !== 'all' ? statusFilter : undefined,
        current_user_id: user?.id,
        is_super_admin: true // Admin sees all
      };

      const result = await simpleQuestionsApi.getQuestions(filters);
      
      if (result.success) {
        setQuestions(result.data || []);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuestions = questions.filter(q => {
    if (!searchTerm) return true;
    return (
      q.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const getStatusConfig = (status) => {
    const config = {
      open: { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-800', label: 'Open', icon: Clock },
      answered: { bg: 'bg-green-50 border-green-200', text: 'text-green-800', label: 'Answered', icon: CheckCircle },
      duplicate: { bg: 'bg-purple-50 border-purple-200', text: 'text-purple-800', label: 'Duplicate', icon: AlertCircle },
      out_of_scope: { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-800', label: 'Out of Scope', icon: AlertCircle },
      closed: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800', label: 'Closed', icon: CheckCircle }
    };
    return config[status] || config.open;
  };

  const handleQuestionClick = (questionId) => {
    navigate(`/questions/detail/${questionId}`);
  };

  return (
    <div className="h-full bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Questions Administration</h1>
        <p className="text-gray-600 mt-1">Manage dealroom and support questions across the platform</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('dealroom')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'dealroom'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Folder className="h-4 w-4" />
                <span>Dealroom Questions</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('support')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'support'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <HelpCircle className="h-4 w-4" />
                <span>Support Questions</span>
              </div>
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab === 'dealroom' ? 'dealroom' : 'support'} questions...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="answered">Answered</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        {/* Questions List */}
        <div className="p-4 max-h-[calc(100vh-350px)] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading questions...</p>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
              <p className="text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'No questions match your current filters' 
                  : `No ${activeTab === 'dealroom' ? 'dealroom' : 'support'} questions yet`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredQuestions.map((question) => {
                const statusConfig = getStatusConfig(question.status);
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div
                    key={question.id}
                    onClick={() => handleQuestionClick(question.id)}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1">
                          {question.title}
                        </h3>
                        
                        {/* Dealroom Context Path */}
                        {activeTab === 'dealroom' && (
                          <div className="text-sm text-blue-600 mb-2">
                            {question.module_reference_name || 'Dealroom'}
                            {question.folder_name && ` > ${question.folder_name}`}
                            {question.file_name && ` > ${question.file_name}`}
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                          {question.description?.replace(/\\n/g, ' ').substring(0, 150)}...
                        </p>
                      </div>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 flex-wrap gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${statusConfig.bg} ${statusConfig.text}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </span>
                        {question.priority === 'high' && (
                          <span className="text-xs px-2 py-1 rounded border bg-red-50 text-red-800 border-red-200">
                            High Priority
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {question.user_name || 'Unknown User'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDate(question.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionsAdminPage;
